import type {
	DiscriminatorFeature,
	DiscriminatorSpecFields,
} from "@sitescene/types";

import { useState } from "react";

import { Button, Field, Select, TextArea, TextInput } from "@/components/field";

import { ListInput } from "./ListInput";
import { FEATURE_KEY_PATTERN } from "./taxonomy-view";

interface SpecEditorProps {
	busy: boolean;
	/** Class ids to offer as confusers, excluding the class itself. */
	classOptions: { label: string; value: string }[];
	error: Error | null;
	/**
	 * Keys the live spec already has. Those are locked: a key is what past
	 * verifications and calibration refer to, so renaming one is remove + add.
	 */
	lockedKeys: string[];
	onRevert?: () => void;
	onSave: (fields: DiscriminatorSpecFields) => void;
	/** Label for the id prefix of every control. */
	prefix: string;
	staged: boolean;
	value: DiscriminatorSpecFields;
}

const EMPTY_FEATURE: DiscriminatorFeature = {
	description: "",
	key: "",
	measurable: false,
};

/**
 * Confusers and features for one spec. Holds its edits locally until saved to
 * the draft; the parent remounts it (by `key`) when the value it should start
 * from changes.
 */
export const SpecEditor = ({
	busy,
	classOptions,
	error,
	lockedKeys,
	onRevert,
	onSave,
	prefix,
	staged,
	value,
}: SpecEditorProps) => {
	const [fields, setFields] = useState(value);
	const locked = new Set(lockedKeys);
	const setFeature = (index: number, changes: Partial<DiscriminatorFeature>) =>
		setFields((current) => ({
			...current,
			features: current.features.map((feature, i) =>
				i === index ? { ...feature, ...changes } : feature,
			),
		}));
	const move = (index: number, offset: number) =>
		setFields((current) => {
			const features = [...current.features];
			const [moved] = features.splice(index, 1);
			features.splice(index + offset, 0, moved!);
			return { ...current, features };
		});

	const keys = fields.features.map((feature) => feature.key.trim());
	const badKey = keys.find(
		(key, i) => !FEATURE_KEY_PATTERN.test(key) || keys.indexOf(key) !== i,
	);
	const dirty = JSON.stringify(fields) !== JSON.stringify(value);

	return (
		<div className="flex flex-col gap-3">
			<label className="flex items-center gap-2 text-sm">
				<input
					checked={fields.active}
					onChange={(event) =>
						setFields({ ...fields, active: event.target.checked })
					}
					type="checkbox"
				/>
				<span className="text-primary">Spec active</span>
			</label>
			<Field htmlFor={`${prefix}-spec-confusers`} label="Look-alikes">
				<ListInput
					id={`${prefix}-spec-confusers`}
					onChange={(confusers) => setFields({ ...fields, confusers })}
					options={classOptions}
					values={fields.confusers}
				/>
			</Field>

			<ol className="flex flex-col gap-3">
				{fields.features.map((feature, index) => {
					const id = `${prefix}-feature-${index}`;
					const keyLocked = locked.has(feature.key);
					return (
						<li
							className="border-secondary flex flex-col gap-2 rounded border p-3"
							key={index}
						>
							<div className="flex flex-wrap items-end gap-3">
								<Field htmlFor={`${id}-key`} label="Key">
									<TextInput
										disabled={keyLocked}
										id={`${id}-key`}
										onChange={(event) =>
											setFeature(index, { key: event.target.value })
										}
										placeholder="rigid-straight-object"
										value={feature.key}
									/>
								</Field>
								<Field htmlFor={`${id}-group`} label="Group (correlated cues)">
									<TextInput
										id={`${id}-group`}
										onChange={(event) =>
											setFeature(index, { group: event.target.value })
										}
										value={feature.group ?? ""}
									/>
								</Field>
								<Field htmlFor={`${id}-polarity`} label="Seeing it points to">
									<Select
										id={`${id}-polarity`}
										onChange={(event) =>
											setFeature(index, {
												polarity: event.target
													.value as DiscriminatorFeature["polarity"],
											})
										}
										value={feature.polarity ?? "candidate"}
									>
										<option value="candidate">the class</option>
										<option value="confuser">a look-alike</option>
									</Select>
								</Field>
								<label className="flex items-center gap-2 pb-1 text-sm">
									<input
										checked={feature.measurable}
										onChange={(event) =>
											setFeature(index, { measurable: event.target.checked })
										}
										type="checkbox"
									/>
									<span className="text-primary">Measurable</span>
								</label>
								<div className="ml-auto flex gap-1">
									<Button
										disabled={index === 0}
										onClick={() => move(index, -1)}
										type="button"
										variant="secondary"
									>
										↑
									</Button>
									<Button
										disabled={index === fields.features.length - 1}
										onClick={() => move(index, 1)}
										type="button"
										variant="secondary"
									>
										↓
									</Button>
									<Button
										onClick={() =>
											setFields({
												...fields,
												features: fields.features.filter((_, i) => i !== index),
											})
										}
										type="button"
										variant="danger"
									>
										Remove
									</Button>
								</div>
							</div>
							<Field htmlFor={`${id}-description`} label="What to look at">
								<TextArea
									id={`${id}-description`}
									onChange={(event) =>
										setFeature(index, { description: event.target.value })
									}
									rows={2}
									value={feature.description}
								/>
							</Field>
						</li>
					);
				})}
			</ol>

			<div className="flex flex-wrap items-center gap-2">
				<Button
					onClick={() =>
						setFields({
							...fields,
							features: [...fields.features, EMPTY_FEATURE],
						})
					}
					type="button"
					variant="secondary"
				>
					Add feature
				</Button>
				<Button
					disabled={busy || !dirty || Boolean(badKey !== undefined)}
					onClick={() => onSave(fields)}
					type="button"
				>
					Save features to draft
				</Button>
				{staged && onRevert && (
					<Button
						disabled={busy}
						onClick={onRevert}
						type="button"
						variant="danger"
					>
						Remove from draft
					</Button>
				)}
				{badKey !== undefined && (
					<span className="text-error-primary text-sm" role="alert">
						Feature keys must be unique lowercase kebab-case
						{badKey ? ` ("${badKey}")` : ""}.
					</span>
				)}
				{error && (
					<span className="text-error-primary text-sm" role="alert">
						{error.message}
					</span>
				)}
			</div>
		</div>
	);
};
